import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import {
  HomeIcon,
  BoltIcon,
  BeakerIcon,
  WifiIcon,
  PlayIcon,
} from "@heroicons/react/24/outline";
import { useInViewReveal } from "@/hooks/useInViewReveal";

interface PayColumn {
  icon: ReactNode;
  titleKey: string;
  descKey: string;
  items: string[];
}

const columns: PayColumn[] = [
  {
    icon: <HomeIcon className="w-12 h-12 text-homeowners-headline" />,
    titleKey: "WhoWePay.Col1Title",
    descKey: "WhoWePay.Col1Desc",
    items: ["WhoWePay.Col1Item1", "WhoWePay.Col1Item2"],
  },
  {
    icon: <BoltIcon className="w-12 h-12 text-homeowners-headline" />,
    titleKey: "WhoWePay.Col2Title",
    descKey: "WhoWePay.Col2Desc",
    items: [
      "WhoWePay.Col2Item1",
      "WhoWePay.Col2Item2",
      "WhoWePay.Col2Item3",
      "WhoWePay.Col2Item4",
    ],
  },
  {
    icon: <BeakerIcon className="w-12 h-12 text-homeowners-headline" />,
    titleKey: "WhoWePay.Col3Title",
    descKey: "WhoWePay.Col3Desc",
    items: [
      "WhoWePay.Col3Item1",
      "WhoWePay.Col3Item2",
      "WhoWePay.Col3Item3",
      "WhoWePay.Col3Item4",
      "WhoWePay.Col3Item5",
    ],
  },
  {
    icon: <WifiIcon className="w-12 h-12 text-homeowners-headline" />,
    titleKey: "WhoWePay.Col4Title",
    descKey: "WhoWePay.Col4Desc",
    items: [
      "WhoWePay.Col4Item1",
      "WhoWePay.Col4Item2",
      "WhoWePay.Col4Item3",
      "WhoWePay.Col4Item4",
      "WhoWePay.Col4Item5",
    ],
  },
  {
    icon: <PlayIcon className="w-12 h-12 text-homeowners-headline" />,
    titleKey: "WhoWePay.Col5Title",
    descKey: "WhoWePay.Col5Desc",
    items: [
      "WhoWePay.Col5Item1",
      "WhoWePay.Col5Item2",
      "WhoWePay.Col5Item3",
      "WhoWePay.Col5Item4",
      "WhoWePay.Col5Item5",
    ],
  },
];

export const WhoWePaySection = () => {
  const [t] = useTranslation("homeowners");
  const { ref: sectionRef, hasRevealed } = useInViewReveal({ threshold: 0.15 });

  return (
    <section
      id="who-we-pay"
      ref={sectionRef}
      className="w-full bg-[#f9f9f6] px-6 sm:px-12 lg:px-24 pt-16 lg:pt-24 pb-20 lg:pb-32"
    >
      {/* Title */}
      <h3
        className={classNames(
          "font-minion text-homeowners-headline text-center leading-snug text-[clamp(1.5rem,0.8rem+3vw,3.75rem)] transition-all duration-[280ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)]",
          {
            "opacity-100 translate-y-0": hasRevealed,
            "opacity-0 translate-y-[12px]": !hasRevealed,
          }
        )}
      >
        {t("WhoWePay.Title")}
      </h3>

      {/* 5-column grid */}
      <div
        className={classNames(
          "mt-12 lg:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 transition-all duration-[280ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)] delay-[100ms]",
          {
            "opacity-100 translate-y-0": hasRevealed,
            "opacity-0 translate-y-[12px]": !hasRevealed,
          }
        )}
      >
        {columns.map((col, idx) => (
          <div
            key={col.titleKey}
            className={classNames(
              "flex flex-col items-center gap-6 lg:gap-8 px-5 lg:px-6 py-8 lg:py-0",
              {
                "border-l border-r border-[#b7b7b7]": idx === 0,
                "border-r border-[#b7b7b7]": idx > 0,
                "max-sm:border-l max-sm:border-r max-sm:border-b max-sm:border-[#b7b7b7] sm:max-lg:border-b sm:max-lg:border-[#b7b7b7]":
                  true,
                "lg:border-l lg:border-r lg:border-[#b7b7b7]": idx === 0,
                "lg:border-r lg:border-[#b7b7b7] lg:border-l-0": idx > 0,
              }
            )}
          >
            {col.icon}

            <p className="font-minion text-homeowners-headline text-center leading-snug text-[clamp(1.125rem,0.9rem+0.8vw,1.875rem)]">
              {t(col.titleKey)}
            </p>

            <p className="font-montserrat font-bold text-[#1a1a1a] text-center leading-snug text-[clamp(0.813rem,0.7rem+0.4vw,1.25rem)]">
              {t(col.descKey)}
            </p>

            <ul className="list-disc pl-6 w-full">
              {col.items.map((itemKey) => (
                <li
                  key={itemKey}
                  className="font-montserrat font-normal text-[#1a1a1a] leading-snug text-[clamp(0.75rem,0.65rem+0.35vw,1rem)] mb-1 last:mb-0"
                >
                  {t(itemKey)}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
};
